import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';

export interface ComboboxOption {
  value: string;
  label: string;
  /** Extra text matched while typing but not shown in the list. */
  keywords?: string;
}

interface Props {
  /** Selected option value, or '' when nothing is picked yet. */
  value: string;
  onChange: (value: string) => void;
  options: ReadonlyArray<ComboboxOption>;
  disabled?: boolean;
  placeholder?: string;
  emptyLabel?: string;
  className?: string;
}

/** Strip Vietnamese diacritics and separators so "duc" matches "Đức". */
function fold(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/gi, 'd')
    .replace(/[_/]/g, ' ')
    .toLowerCase();
}

/**
 * A dropdown you can also type into: typing filters the list, and only a
 * value from the list can be selected. A `value` with no matching option is
 * still displayed, so bad legacy data stays visible instead of looking empty.
 */
export default function Combobox({
  value,
  onChange,
  options,
  disabled,
  placeholder = 'Gõ để tìm…',
  emptyLabel = 'Không có kết quả.',
  className = '',
}: Props) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const selected = useMemo(
    () => options.find((o) => o.value === value),
    [options, value],
  );

  const matches = useMemo(() => {
    const q = fold(query.trim());
    if (!q) return options;
    return options.filter((o) => fold(`${o.label} ${o.value} ${o.keywords ?? ''}`).includes(q));
  }, [options, query]);

  // Close (and drop the half-typed query) on an outside click.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (wrapRef.current?.contains(e.target as Node)) return;
      setOpen(false);
      setQuery('');
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    listRef.current?.children[highlight]?.scrollIntoView({ block: 'nearest' });
  }, [open, highlight]);

  const commit = (next: string) => {
    onChange(next);
    setQuery('');
    setOpen(false);
  };

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        setHighlight(0);
        return;
      }
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      setHighlight((h) => (matches.length === 0 ? 0 : (h + delta + matches.length) % matches.length));
      return;
    }
    if (e.key === 'Enter' && open) {
      e.preventDefault();
      const pick = matches[highlight];
      if (pick) commit(pick.value);
      return;
    }
    if (e.key === 'Escape' && open) {
      e.preventDefault();
      setOpen(false);
      setQuery('');
    }
  };

  const display = selected?.label ?? value;

  return (
    <div ref={wrapRef} className="relative">
      <input
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        autoComplete="off"
        disabled={disabled}
        value={open ? query : display}
        placeholder={display || placeholder}
        onFocus={() => {
          setOpen(true);
          setHighlight(Math.max(0, matches.findIndex((o) => o.value === value)));
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setHighlight(0);
        }}
        onKeyDown={handleKeyDown}
        className={className}
      />

      {open && (
        <ul
          ref={listRef}
          role="listbox"
          className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg py-1 text-sm"
        >
          {matches.length === 0 ? (
            <li className="px-3 py-2 text-gray-400">{emptyLabel}</li>
          ) : (
            matches.map((o, i) => (
              <li key={o.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={o.value === value}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => commit(o.value)}
                  className={`w-full text-left px-3 py-2 transition-colors ${
                    i === highlight ? 'bg-red-50 text-red-700' : 'hover:bg-gray-50'
                  } ${o.value === value ? 'font-semibold' : ''}`}
                >
                  {o.label}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
